import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Users, BellRing, WifiOff } from 'lucide-react';
import { mozoApi, mensajeDeError } from './mozoApi';
import { useMozo } from './MozoSesion';
import usePolling from './usePolling';

const COLOR_ESTADO = {
  LIBRE: 'border-emerald-300 bg-emerald-50',
  OCUPADA: 'border-amber-300 bg-amber-50',
  FUERA_DE_SERVICIO: 'border-gray-200 bg-gray-100 opacity-60',
};

export default function MozoMesas() {
  const { usuario, salir } = useMozo();
  const navigate = useNavigate();
  const location = useLocation();
  const [salones, setSalones] = useState([]);
  const [salonId, setSalonId] = useState(null);
  const [soloMias, setSoloMias] = useState(false);
  const [avisos, setAvisos] = useState([]);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState(location.state?.aviso || '');

  useEffect(() => {
    if (location.state?.aviso) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const cargar = useCallback(async () => {
    try {
      const [respSalones, respAvisos] = await Promise.all([
        mozoApi.get('/restaurante/salones'),
        mozoApi.get('/restaurante/avisos'),
      ]);
      const lista = Array.isArray(respSalones.data) ? respSalones.data : [];

      setSalones(lista);
      setAvisos(Array.isArray(respAvisos.data) ? respAvisos.data : []);
      setSalonId((actual) =>
        actual && lista.some((s) => s.id === actual) ? actual : lista[0]?.id ?? null
      );
      setError('');
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudieron cargar las mesas.'));
    }
  }, []);

  usePolling(cargar, 10000);

  const salon = salones.find((s) => s.id === salonId);
  const mesas = (salon?.mesas || []).filter((mesa) => {
    if (!soloMias) return true;

    return (mesa.sesiones || []).some(
      (sesion) => sesion.estado === 'ABIERTA' && sesion.usuario_apertura_id === usuario?.id
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="sticky top-0 z-10 flex items-center justify-between bg-green-800 px-4 py-3 text-white">
        <div>
          <p className="text-xs opacity-80">Mozo</p>
          <p className="text-lg font-extrabold leading-tight">{usuario?.nombre}</p>
        </div>
        <button
          type="button"
          onClick={salir}
          className="flex items-center gap-1 rounded-lg bg-white/15 px-3 py-2 text-sm font-bold"
        >
          <LogOut size={16} /> Salir
        </button>
      </header>

      {error && (
        <p
          role="alert"
          className="flex items-center gap-2 bg-red-600 px-4 py-2 text-sm font-bold text-white"
        >
          <WifiOff size={16} /> {error}
        </p>
      )}

      {mensaje && (
        <button
          type="button"
          onClick={() => setMensaje('')}
          className="w-full bg-emerald-600 px-4 py-2 text-left text-sm font-bold text-white"
        >
          {mensaje}
        </button>
      )}

      {avisos.length > 0 && (
        <div className="space-y-2 px-4 pt-3">
          {avisos.map((aviso) => (
            <Link
              key={aviso.id}
              to={`/mozo/mesa/${aviso.mesa?.id}`}
              className="flex items-center gap-2 rounded-xl bg-amber-400 px-4 py-3 text-base font-extrabold text-gray-900"
            >
              <BellRing size={20} />
              Pedido listo: {aviso.mesa?.alias || aviso.mesa?.nombre}
            </Link>
          ))}
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto px-4 pt-4">
        {salones.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSalonId(s.id)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold ${
              s.id === salonId ? 'bg-green-700 text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {s.nombre}
          </button>
        ))}
      </div>

      <label className="flex items-center gap-2 px-4 pt-4 text-sm font-bold text-gray-700">
        <input
          type="checkbox"
          checked={soloMias}
          onChange={(e) => setSoloMias(e.target.checked)}
          className="h-5 w-5"
        />
        Solo mis mesas
      </label>

      <main className="grid grid-cols-2 gap-3 px-4 pt-4">
        {mesas.map((mesa) => {
          const sesion = (mesa.sesiones || []).find((s) => s.estado === 'ABIERTA');
          const fuera = mesa.estado === 'FUERA_DE_SERVICIO';
          const clase = `rounded-2xl border-2 p-4 text-left shadow-sm ${
            COLOR_ESTADO[mesa.estado] || COLOR_ESTADO.LIBRE
          }`;
          const contenido = (
            <>
              <p className="text-lg font-extrabold text-gray-900">{mesa.nombre}</p>
              {mesa.alias && <p className="text-sm font-bold text-green-800">{mesa.alias}</p>}
              <p className="mt-2 flex items-center gap-1 text-sm text-gray-600">
                <Users size={14} />
                {sesion ? `${sesion.comensales} comensales` : `Capacidad ${mesa.capacidad}`}
              </p>
              <p className="mt-2 text-xs font-bold uppercase text-gray-600">
                {fuera ? 'Fuera de servicio' : sesion ? 'Ocupada' : 'Libre'}
              </p>
            </>
          );

          return fuera ? (
            <div key={mesa.id} className={clase}>
              {contenido}
            </div>
          ) : (
            <Link key={mesa.id} to={`/mozo/mesa/${mesa.id}`} className={clase}>
              {contenido}
            </Link>
          );
        })}

        {!mesas.length && !error && (
          <p className="col-span-2 py-8 text-center text-sm text-gray-500">
            {soloMias ? 'No tenés mesas abiertas.' : 'No hay mesas en este salón.'}
          </p>
        )}
      </main>
    </div>
  );
}
